import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { api } from "@/api";
import {
  CalendarDays,
  Home,
  Bed,
  AlertTriangle,
  FileText,
  Zap,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  X,
  CheckCircle2,
} from "lucide-react";

// 6 étapes du tutoriel
export const TUTORIAL_STEPS = [
  {
    key: "planning",
    image: "/tutorial/step1.png",
    icon: CalendarDays,
    emoji: "📅",
    title: "Votre Planning",
    body: (
      <p>
        Votre planning affiche tous vos séjours passés et à venir. Basculez entre <strong>vue liste</strong>{" "}
        (chronologique) et <strong>vue calendrier</strong> (mensuelle) selon votre préférence. Les séjours sont
        colorés par type : locataire ou propriétaire. Cliquez sur un séjour pour consulter son détail complet.
      </p>
    ),
  },
  {
    key: "stay-type",
    image: "/tutorial/step2.png",
    icon: Home,
    emoji: "🏠",
    title: "Type de séjour : Propriétaire ou Locataire",
    body: (
      <div className="space-y-3">
        <p>Lors de la création d&apos;un séjour, choisissez le type :</p>
        <div className="rounded-lg border border-violet-500/20 bg-violet-500/5 p-3">
          <p>
            <span className="font-medium text-violet-300">🏠 Séjour Propriétaire</span> : vous occupez votre logement.
            Vous pouvez effectuer vous-même le ménage de fin de séjour, ou le confier à BDM (à préciser lors de la
            création).
          </p>
        </div>
        <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3">
          <p>
            <span className="font-medium text-emerald-300">👥 Séjour Locataire</span> : un voyageur occupe le logement.
            Le téléphone est utile, mais <strong>l&apos;email du voyageur</strong> permet à BDM de proposer au voyageur
            des services complémentaires tels que le <strong>transfert aéroport/gare</strong>, la{" "}
            <strong>location de matériel de ski avec livraison au logement</strong>, et d&apos;autres prestations
            disponibles. Sans email valide, ces services ne peuvent pas être proposés.
          </p>
        </div>
      </div>
    ),
  },
  {
    key: "services",
    image: "/tutorial/step3.png",
    icon: Bed,
    emoji: "🛏️",
    title: "Services & Linge",
    body: (
      <div className="space-y-2">
        <p>Pour chaque séjour, sélectionnez les prestations souhaitées :</p>
        <ul className="space-y-2 text-sm">
          <li>
            <span className="font-medium text-slate-100">🧹 Ménage</span> : inclus par défaut pour les locataires. Pour
            un séjour propriétaire, cochez si vous souhaitez le confier à BDM.
          </li>
          <li>
            <span className="font-medium text-slate-100">🛏️ Kit linge simple (1 pers.) / Kit linge double (2 pers.)</span>
            {" "}: jeux de draps et serviettes fournis par BDM. Indiquez le nombre de kits selon la configuration du
            logement.
          </li>
          <li>
            <span className="font-medium text-slate-100">✅ Lits faits</span> : BDM prépare les lits avant l&apos;arrivée.{" "}
            <span className="text-amber-300">⚠️ Option verrouillée sans kit linge</span> — nécessite obligatoirement un
            kit simple ou double commandé.
          </li>
          <li>
            <span className="font-medium text-slate-100">🧴 Kit hygiène</span> : papier toilette, éponge, savon,
            pastilles lave-vaisselle, liquide vaisselle, sacs poubelle petit format et grand format, et échantillons
            de produits de toilette (shampoing, gel douche, après-shampoing). Tout est déposé dans le logement avant
            l&apos;arrivée du voyageur.
          </li>
          <li>
            <span className="font-medium text-slate-100">🧺 Panier de bienvenue</span> : sélectionnez la formule
            souhaitée.
          </li>
          <li>
            <span className="font-medium text-slate-100">👶 Puériculture</span> : lit bébé, chaise haute — cochez les
            équipements nécessaires.
          </li>
        </ul>
      </div>
    ),
  },
  {
    key: "7-days-rule",
    image: "/tutorial/step4.png",
    icon: AlertTriangle,
    emoji: "⚠️",
    title: "Règle des 7 jours",
    body: (
      <div className="space-y-3">
        <p>
          <strong>Toutes les modifications restent possibles à tout moment.</strong> Cependant, à partir de{" "}
          <strong>J-7</strong> (7 jours avant l&apos;arrivée), les modifications ne peuvent plus être effectuées
          directement.
        </p>
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
          <p>
            Vous devez obligatoirement passer par le bouton{" "}
            <span className="font-medium text-violet-300">&quot;Demande / Modification&quot;</span> sur le séjour
            concerné pour soumettre votre demande à BDM, qui la traitera manuellement.
          </p>
        </div>
        <p className="text-sm text-slate-300">En cas d&apos;urgence, contactez-nous directement par téléphone.</p>
      </div>
    ),
  },
  {
    key: "billing",
    image: "/tutorial/step5.png",
    icon: FileText,
    emoji: "📄",
    title: "Facturation",
    body: (
      <div className="space-y-2 text-sm">
        <p>L&apos;onglet <strong>Facturation</strong> centralise toutes vos factures BDM.</p>
        <ul className="space-y-2">
          <li>
            <span className="font-medium text-slate-100">📬 Email automatique</span> : dès qu&apos;une nouvelle facture
            est disponible, vous recevez un email de BDM avec la facture en pièce jointe. Vous n&apos;avez rien à
            faire.
          </li>
          <li>
            <span className="font-medium text-slate-100">🔴 Non soldée / 🟢 Soldée</span> : chaque facture affiche
            clairement son statut de paiement. Une facture non soldée est en attente de règlement (paiement sous 15
            jours).
          </li>
          <li>
            <span className="font-medium text-slate-100">📥 Téléchargement</span> : cliquez sur l&apos;icône
            téléchargement pour obtenir le PDF à tout moment, y compris les factures anciennes.
          </li>
          <li>
            <span className="font-medium text-slate-100">📊 Statistiques</span> : l&apos;onglet Statistiques vous donne
            une vue de vos coûts par période, par logement, et le détail des prestations facturées.
          </li>
        </ul>
      </div>
    ),
  },
  {
    key: "supplements",
    image: "/tutorial/step6.png",
    icon: Zap,
    emoji: "⚡",
    title: "Suppléments",
    body: (
      <div className="space-y-2 text-sm">
        <p>L&apos;onglet <strong>Suppléments</strong> regroupe deux types d&apos;éléments signalés par BDM après un séjour :</p>
        <ul className="space-y-2">
          <li>
            <span className="font-medium text-amber-300">⚡ Interventions</span> : travaux, réparations ou actions
            spécifiques réalisées dans votre logement (remplacement d&apos;ampoule, débouchage, intervention
            technique…).
          </li>
          <li>
            <span className="font-medium text-violet-300">💶 Surcoûts</span> : frais additionnels constatés après un
            séjour (casse, nettoyage exceptionnel, produits manquants…).
          </li>
        </ul>
        <p>
          Un <span className="font-medium text-rose-300">badge de notification rouge</span> apparaît sur l&apos;onglet
          dès qu&apos;un nouveau supplément vous concerne.
        </p>
        <p>
          <span className="font-medium text-slate-100">📬 Email automatique</span> : dès qu&apos;un supplément est
          constaté et enregistré, vous recevez un email de BDM avec les informations complémentaires.
        </p>
      </div>
    ),
  },
  {
    key: "calendars-services",
    image: "/tutorial/step7.png",
    icon: CalendarRange,
    emoji: "🗓️",
    title: "Calendriers iCal & Services par défaut",
    body: (
      <div className="space-y-3 text-sm">
        <p>
          L&apos;onglet <strong>Calendriers & Services</strong> (dans la barre de navigation) automatise la
          détection de vos nouvelles réservations Airbnb / Booking / Abritel / VRBO / Gîtes de France.
        </p>

        <div className="rounded-lg border border-violet-500/20 bg-violet-500/5 p-3 space-y-1">
          <div className="font-medium text-violet-300">🗓️ Mes calendriers iCal</div>
          <p>
            Pour chaque logement, copiez l&apos;URL iCal fournie par votre plateforme (Airbnb : <em>Calendrier
            → Exporter le calendrier</em> ; Booking : <em>Tarifs et disponibilités → Synchroniser →
            Synchronisation iCal</em>) et collez-la dans une nouvelle ligne. Activez le toggle <strong>Actif</strong>
            puis cliquez <strong>Sauvegarder</strong>.
          </p>
          <p>
            BDM synchronise automatiquement <strong>une fois par jour</strong>. Le bouton{" "}
            <span className="font-medium text-emerald-300">🔄 Synchroniser maintenant</span> force la synchro
            immédiatement (utile pour tester votre URL).
          </p>
          <p>
            À chaque nouvelle réservation détectée, vous recevez un email avec un lien direct vers la fiche.
            Le séjour apparaît dans votre planning avec un badge orange{" "}
            <span className="font-medium text-amber-300">⏰ À compléter</span>.
          </p>
        </div>

        <div className="rounded-lg border border-fuchsia-500/20 bg-fuchsia-500/5 p-3 space-y-1">
          <div className="font-medium text-fuchsia-300">✨ Services par défaut</div>
          <p>
            Configurez les prestations habituelles que vous commandez pour chaque locataire (kit linge simple,
            kit linge double, lits faits, kit hygiène).
          </p>
          <p>
            Cochez la case violette{" "}
            <strong>« Appliquer automatiquement ces services à chaque réservation iCal détectée »</strong> pour
            que ces options soient pré-cochées sur chaque nouveau séjour draft. Si vous laissez la case décochée,
            chaque réservation arrive sans aucun service présélectionné et vous devrez les ajouter manuellement
            au moment de la complétion.
          </p>
        </div>

        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
          <div className="font-medium text-amber-200">📝 Compléter un séjour draft</div>
          <p>
            Sur le séjour orange, cliquez sur <strong>« Compléter »</strong> : renseignez les informations du
            voyageur (<strong>le nom et l&apos;email sont obligatoires</strong>, l&apos;email permet à BDM
            d&apos;envoyer un accusé de réception et de proposer ses services complémentaires), ajustez les
            services puis cliquez sur <strong>✅ Confirmer le séjour</strong>. BDM est alors notifié et
            organise tout.
          </p>
        </div>

        <p className="text-xs text-slate-400">
          ⚠️ Si vous oubliez de confirmer un séjour, BDM vous envoie un rappel automatique 48h après la détection,
          puis un second rappel 48h plus tard (maximum 2 relances).
        </p>
      </div>
    ),
  },
];

export default function OwnerTutorial({ startStep = 0, onClose, required = true }) {
  const initial = Math.min(Math.max(0, startStep), TUTORIAL_STEPS.length - 1);
  const [step, setStep] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const isLast = step === TUTORIAL_STEPS.length - 1;
  const isFirst = step === 0;
  const current = TUTORIAL_STEPS[step];
  const Icon = current.icon;
  const [imageVisible, setImageVisible] = useState(true);

  // Reset visibility when step changes
  React.useEffect(() => {
    setImageVisible(true);
  }, [step]);

  const goPrev = () => setStep((s) => Math.max(0, s - 1));
  const goNext = () => setStep((s) => Math.min(TUTORIAL_STEPS.length - 1, s + 1));

  const finish = async () => {
    if (!required) {
      onClose?.();
      return;
    }
    try {
      setSubmitting(true);
      await api.post("/owner/tutorial/complete");
      toast({
        title: "🎉 Bienvenue chez Blanc des Montagnes !",
        description: "Vous êtes prêt à utiliser votre portail. Bon séjour dans nos montagnes !",
      });
      onClose?.();
    } catch (e) {
      toast({
        title: "Erreur",
        description: "Impossible d'enregistrer la fin du tutoriel. Réessayez.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Bloque le clic en dehors si required
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !required) {
      onClose?.();
    }
  };

  return (
    <div
      data-testid="owner-tutorial-overlay"
      onClick={handleBackdropClick}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
    >
      <div
        data-testid="owner-tutorial-card"
        className="relative w-full max-w-2xl rounded-2xl border border-white/10 bg-[#0f1320] text-slate-100 shadow-2xl max-h-[90vh] flex flex-col"
      >
        {/* Croix de fermeture en mode replay */}
        {!required && (
          <button
            data-testid="owner-tutorial-close"
            onClick={() => onClose?.()}
            className="absolute top-3 right-3 z-10 rounded-full p-2 text-slate-400 hover:bg-white/10 hover:text-white transition-colors"
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
        )}

        {/* Header progress */}
        <div className="border-b border-white/10 p-5 pr-12">
          <div className="flex items-center justify-between mb-3">
            <div data-testid="owner-tutorial-progress-label" className="text-xs font-medium text-violet-300">
              Étape {step + 1} / {TUTORIAL_STEPS.length}
            </div>
            <div className="text-xs text-slate-400">{required ? "Bienvenue 👋" : "Guide d'utilisation"}</div>
          </div>
          <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
            <div
              data-testid="owner-tutorial-progress-bar"
              className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500 transition-all duration-300"
              style={{ width: `${((step + 1) / TUTORIAL_STEPS.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Contenu étape */}
        <div data-testid="owner-tutorial-content" className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-violet-500/15 border border-violet-500/30 p-3">
              <Icon className="h-7 w-7 text-violet-300" />
            </div>
            <h2 className="text-xl font-semibold text-white">
              <span className="mr-2">{current.emoji}</span>
              {current.title}
            </h2>
          </div>

          {/* Illustration de l'étape (masquée si fichier absent) */}
          {current.image && imageVisible && (
            <div className="rounded-xl border border-white/10 bg-black/30 p-2 overflow-hidden">
              <img
                data-testid={`owner-tutorial-image-${step}`}
                src={current.image}
                alt={`Illustration : ${current.title}`}
                onError={() => setImageVisible(false)}
                className="block w-full max-h-72 object-contain rounded-lg"
              />
            </div>
          )}

          <div className="text-slate-200 leading-relaxed pl-1">{current.body}</div>
        </div>

        {/* Footer actions */}
        <div className="border-t border-white/10 p-4 flex items-center justify-between gap-2">
          <Button
            data-testid="owner-tutorial-prev"
            variant="outline"
            className="border-white/10 bg-white/[0.05] text-slate-100 hover:bg-white/10"
            onClick={goPrev}
            disabled={isFirst}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Précédent
          </Button>

          {/* Dots indicators */}
          <div className="hidden sm:flex items-center gap-1.5">
            {TUTORIAL_STEPS.map((s, i) => (
              <button
                key={s.key}
                onClick={() => setStep(i)}
                data-testid={`owner-tutorial-dot-${i}`}
                className={`h-2 rounded-full transition-all ${
                  i === step ? "w-6 bg-violet-400" : i < step ? "w-2 bg-violet-500/50" : "w-2 bg-white/15"
                }`}
                aria-label={`Aller à l'étape ${i + 1}`}
              />
            ))}
          </div>

          {isLast ? (
            <Button
              data-testid="owner-tutorial-finish"
              onClick={finish}
              disabled={submitting}
              className="bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white hover:from-violet-400 hover:to-fuchsia-400"
            >
              <CheckCircle2 className="h-4 w-4 mr-1" />
              {submitting ? "Enregistrement..." : "Terminer"}
            </Button>
          ) : (
            <Button
              data-testid="owner-tutorial-next"
              onClick={goNext}
              className="bg-violet-500 text-white hover:bg-violet-400"
            >
              Suivant
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
